import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { BusinessSector, BusinessSectorInsert } from '@/services/content'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Loader2 } from 'lucide-react'
import { sectorFormSchema, SectorFormValues } from './sector/SectorSchema'
import { BasicTab } from './sector/BasicTab'
import { ContentTab } from './sector/ContentTab'
import { ListsTab } from './sector/ListsTab'
import { SolutionsTab } from './sector/SolutionsTab'
import { FormConfigTab } from './sector/FormConfigTab'

interface BusinessAreaDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialData?: BusinessSector | null
  onSubmit: (data: BusinessSectorInsert) => Promise<void>
  isSubmitting: boolean
  locale: string
}

export const BusinessAreaDialog = ({
  open,
  onOpenChange,
  initialData,
  onSubmit,
  isSubmitting,
  locale,
}: BusinessAreaDialogProps) => {
  const form = useForm<SectorFormValues>({
    resolver: zodResolver(sectorFormSchema),
    defaultValues: {
      title: '',
      description: '',
      icon_name: 'Globe',
      slug: '',
      image_url: null,
      sort_order: 0,
      hero_title: '',
      hero_cta: 'Request Audit',
      seo_title: '',
      seo_description: '',
      seo_keywords: '',
      problems: [],
      ai_capabilities: [],
      saas_examples: [],
      business_impact: [],
      show_employee_count: true,
      show_uses_excel: true,
      custom_challenge_label: '',
    },
  })

  useEffect(() => {
    if (initialData) {
      const pc = initialData.page_content || {}
      form.reset({
        title: initialData.title,
        description: initialData.description,
        icon_name: initialData.icon_name,
        slug: initialData.slug || '',
        image_url: initialData.image_url,
        sort_order: initialData.sort_order,
        hero_title: pc.hero?.title || '',
        hero_cta: pc.hero?.cta || '',
        seo_title: pc.seo?.title || '',
        seo_description: pc.seo?.description || '',
        seo_keywords: pc.seo?.keywords || '',
        problems: (pc.problems || []).map((p: string) => ({ value: p })),
        ai_capabilities: (pc.ai_capabilities || []).map((a: string) => ({
          value: a,
        })),
        saas_examples: pc.saas_examples || [],
        business_impact: pc.business_impact || [],
        show_employee_count: pc.form_config?.show_employee_count ?? true,
        show_uses_excel: pc.form_config?.show_uses_excel ?? true,
        custom_challenge_label: pc.form_config?.custom_challenge_label || '',
      })
    } else {
      form.reset({
        title: '',
        description: '',
        icon_name: 'Globe',
        slug: '',
        image_url: null,
        sort_order: 0,
        hero_title: '',
        hero_cta: 'Request Audit',
        seo_title: '',
        seo_description: '',
        seo_keywords: '',
        problems: [],
        ai_capabilities: [],
        saas_examples: [],
        business_impact: [],
        show_employee_count: true,
        show_uses_excel: true,
        custom_challenge_label: '',
      })
    }
  }, [initialData, form, open])

  const handleSubmit = async (values: SectorFormValues) => {
    // Reconstruct nested object
    const payload: BusinessSectorInsert = {
      title: values.title,
      description: values.description,
      icon_name: values.icon_name,
      slug: values.slug,
      image_url: values.image_url,
      sort_order: values.sort_order,
      locale,
      page_content: {
        hero: {
          title: values.hero_title,
          cta: values.hero_cta,
        },
        seo: {
          title: values.seo_title,
          description: values.seo_description || '',
          keywords: values.seo_keywords || '',
        },
        problems: values.problems.map((p) => p.value),
        ai_capabilities: values.ai_capabilities.map((p) => p.value),
        saas_examples: values.saas_examples,
        business_impact: values.business_impact,
        form_config: {
          show_employee_count: values.show_employee_count,
          show_uses_excel: values.show_uses_excel,
          custom_challenge_label: values.custom_challenge_label || undefined,
        },
      },
    }

    await onSubmit(payload)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] h-[90vh] flex flex-col p-0 gap-0">
        <div className="p-6 border-b">
          <DialogHeader>
            <DialogTitle>
              {initialData ? 'Edit Sector' : 'New Sector'} (
              {locale.toUpperCase()})
            </DialogTitle>
            <DialogDescription>
              Configure all aspects of the sector landing page.
            </DialogDescription>
          </DialogHeader>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex-1 overflow-hidden flex flex-col"
          >
            <div className="flex-1 overflow-y-auto p-6">
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="flex flex-wrap h-auto gap-2 mb-6">
                  <TabsTrigger value="basic">Basic Info</TabsTrigger>
                  <TabsTrigger value="content">Content & SEO</TabsTrigger>
                  <TabsTrigger value="lists">Problems & AI</TabsTrigger>
                  <TabsTrigger value="solutions">
                    Solutions & Impact
                  </TabsTrigger>
                  <TabsTrigger value="form">Form Config</TabsTrigger>
                </TabsList>

                <TabsContent value="basic">
                  <BasicTab />
                </TabsContent>
                <TabsContent value="content">
                  <ContentTab />
                </TabsContent>
                <TabsContent value="lists">
                  <ListsTab />
                </TabsContent>
                <TabsContent value="solutions">
                  <SolutionsTab />
                </TabsContent>
                <TabsContent value="form">
                  <FormConfigTab />
                </TabsContent>
              </Tabs>
            </div>

            <div className="p-6 border-t bg-slate-50 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Sector
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
